import { useState, useEffect, useCallback, useRef } from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import H from '../texts/h/H';
import P from '../texts/p/P';
import DropDown, { ItemProp } from '../dropdown/DropDown';
import { 
  uploadAnnexToCasfog, 
  getSubmissionStatusForPeriod,
  PeriodStatus 
} from '../../services/AnnexesService';

interface CasfogUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  annexId: number;
  annexName: string;
  annexCode?: string;
}

const MONTHLY_ANNEXES = ['A', 'B', 'C', 'D', 'E'];
const SNAPSHOT_ANNEXES = ['F1', 'F2', 'G', 'H1', 'H2', 'J1', 'J2', 'K', '6'];
const MAX_FILE_SIZE_MB = 10;

const CasfogUploadModal = ({
  isOpen,
  onClose,
  annexId,
  annexName,
  annexCode,
}: CasfogUploadModalProps) => {
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [submissionType, setSubmissionType] = useState<'ORIGINAL' | 'AMENDMENT'>('ORIGINAL');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
  const [periodStatus, setPeriodStatus] = useState<PeriodStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentYear = new Date().getFullYear();
  const yearItems: ItemProp[] = Array.from({ length: 10 }, (_, i) => ({
    label: (currentYear - i).toString(),
  }));

  const monthItems: ItemProp[] = [
    { label: 'Enero (1)' },
    { label: 'Febrero (2)' },
    { label: 'Marzo (3)' },
    { label: 'Abril (4)' },
    { label: 'Mayo (5)' },
    { label: 'Junio (6)' },
    { label: 'Julio (7)' },
    { label: 'Agosto (8)' },
    { label: 'Septiembre (9)' },
    { label: 'Octubre (10)' },
    { label: 'Noviembre (11)' },
    { label: 'Diciembre (12)' },
  ];

  const submissionTypeItems: ItemProp[] = [
    { label: 'Original' },
    { label: 'Rectificativa' },
  ];

  const isMonthlyAnnex = MONTHLY_ANNEXES.includes(annexCode || '');
  const isSnapshotAnnex = SNAPSHOT_ANNEXES.includes(annexCode || '');

  const fetchPeriodStatus = useCallback(async () => {
    if (!selectedYear || !selectedMonth) return;
    
    setLoadingStatus(true);
    try {
      const status = await getSubmissionStatusForPeriod(annexId, selectedYear, selectedMonth);
      setPeriodStatus(status);
      
      if (status.requiresAmendment) {
        setSubmissionType('AMENDMENT');
      }
    } catch (err) {
      console.error('Error fetching period status:', err);
    } finally {
      setLoadingStatus(false);
    }
  }, [annexId, selectedYear, selectedMonth]);

  useEffect(() => {
    if (isOpen) {
      fetchPeriodStatus();
    }
  }, [isOpen, fetchPeriodStatus]);

  const handleYearChange = (yearLabel: string) => {
    setSelectedYear(parseInt(yearLabel));
    setError(null);
    setPeriodStatus(null);
  };

  const handleMonthChange = (monthLabel: string) => {
    const monthMatch = monthLabel.match(/\((\d+)\)/);
    if (monthMatch) {
      setSelectedMonth(parseInt(monthMatch[1]));
      setError(null);
      setPeriodStatus(null);
    }
  };

  const handleSubmissionTypeChange = (typeLabel: string) => {
    const type = typeLabel === 'Rectificativa' ? 'AMENDMENT' : 'ORIGINAL';
    setSubmissionType(type);
    setError(null);
  };

  const submissionTypeLabel = submissionType === 'ORIGINAL' ? 'Original' : 'Rectificativa';

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Solo se permiten archivos CSV');
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      setError(`El archivo excede el tamaño máximo de ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    const file = event.dataTransfer.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Solo se permiten archivos CSV');
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      setError(`El archivo excede el tamaño máximo de ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Debe seleccionar un archivo');
      return;
    }

    if (periodStatus?.hasPending) {
      setError('Ya existe un envío pendiente para este período. Espere la respuesta de CASFOG.');
      return;
    }

    if (periodStatus?.requiresAmendment && submissionType === 'ORIGINAL') {
      setError('Ya existe un envío exitoso. Debe enviar como Rectificativa.');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const userId = localStorage.getItem('userId') || localStorage.getItem('email') || 'unknown';
      
      const result = await uploadAnnexToCasfog(
        annexId,
        selectedFile,
        selectedYear,
        selectedMonth,
        userId,
        submissionType
      );

      if (result.status === 'PENDING') {
        setSuccess(`Envío procesado exitosamente. Operation ID: ${result.operationId}. Esperando confirmación de CASFOG.`);
        setTimeout(() => {
          onClose();
          resetForm();
        }, 3000);
      } else if (result.errors?.length) {
        setError(`Error: ${result.errors.join(', ')}`);
      } else {
        setSuccess(result.message);
      }
    } catch (err: any) {
      setError(err.message || 'Error al subir el anexo a CASFOG');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    resetForm();
  };

  const resetForm = () => {
    setSelectedYear(new Date().getFullYear());
    setSelectedMonth(new Date().getMonth() + 1);
    setSubmissionType('ORIGINAL');
    setSelectedFile(null);
    setError(null);
    setSuccess(null);
    setUploading(false);
    setPeriodStatus(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getHelperText = () => {
    if (isMonthlyAnnex) {
      return 'Este anexo requiere un período mensual (año + mes). El archivo CSV debe contener los datos correspondientes al período seleccionado.';
    }
    if (isSnapshotAnnex) {
      return 'Este anexo es un snapshot puntual. Seleccione el período de corte para el cual genera el reporte.';
    }
    return 'Seleccione el período y suba el archivo CSV generado por el sistema.';
  };

  const canSubmit = selectedFile && !uploading && !loadingStatus && !periodStatus?.hasPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Enviar Anexo a CASFOG"
      primaryText="Enviar"
      primaryVariant="primary"
      onPrimaryAction={handleUpload}
      primaryActionDisabled={!canSubmit}
      loadingPrimaryBtn={uploading}
      secondaryText="Cancelar"
      secondaryVariant="contained"
      onSecondaryAction={handleCancel}
      width="600px"
      overflow="visible"
    >
      <Card margin={{ top: '3', bottom: '3' }}>
        <Card margin={{ bottom: '4' }}>
          <H type='4' weight='bold' withMargin>
            {annexName}
          </H>
          {annexCode && (
            <P type='2' variant='neutral' withMargin={false}>
              Código: {annexCode}
            </P>
          )}
        </Card>

        <Card margin={{ bottom: '3' }} spacing="2" variant="info" modifier="inverted">
          <P type='2' variant='info' withMargin={false}>{getHelperText()}</P>
        </Card>

        <Card display='flex' gap='16px' margin={{ bottom: '3' }}>
          <Card width='50%'>
            <DropDown
              id="year-selector"
              label="Año"
              title={selectedYear.toString()}
              items={yearItems}
              onChange={handleYearChange}
              size="md"
            />
          </Card>
          <Card width='50%'>
            <DropDown
              id="month-selector"
              label="Mes"
              title={monthItems.find(item => item.label.includes(`(${selectedMonth})`))?.label || "Seleccionar mes"}
              items={monthItems}
              onChange={handleMonthChange}
              size="md"
            />
          </Card>
        </Card>

        {loadingStatus ? (
          <Card margin={{ bottom: '3' }} spacing="2" variant="neutral" modifier="weak">
            <P type='2' withMargin={false}>Verificando estado del período...</P>
          </Card>
        ) : periodStatus && (
          <Card margin={{ bottom: '3' }}>
            {periodStatus.hasPending && (
              <Card spacing="2" variant="warning" modifier="weak">
                <P type='2' withMargin={false}>
                  ⚠️ Ya existe un envío pendiente para este período. Debe esperar la respuesta de CASFOG.
                </P>
              </Card>
            )}
            {periodStatus.hasSuccess && !periodStatus.hasPending && (
              <Card spacing="2" variant="info" modifier="inverted">
                <P type='2' variant='info' withMargin={false}>
                  ℹ️ Ya existe un envío exitoso para este período. El nuevo envío será marcado como Rectificativa.
                </P>
              </Card>
            )}
          </Card>
        )}

        <Card margin={{ bottom: '3' }}>
          <DropDown
            id="submission-type-selector"
            label="Tipo de envío"
            title={submissionTypeLabel}
            items={submissionTypeItems}
            onChange={handleSubmissionTypeChange}
            size="md"
            disabled={periodStatus?.requiresAmendment}
          />
        </Card>

        <Card margin={{ bottom: '3' }}>
          <P type='2' weight='bold' withMargin>Archivo CSV</P>
          <Card
            spacing="4"
            variant="neutral"
            modifier="weak"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{ 
              border: '2px dashed #ccc', 
              borderRadius: '8px',
              cursor: 'pointer',
              minHeight: '120px'
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {selectedFile ? (
              <>
                <P type='2' variant='success' withMargin={false} weight='bold'>
                  ✓ {selectedFile.name}
                </P>
                <P type='3' variant='neutral' withMargin={false}>
                  ({(selectedFile.size / 1024).toFixed(1)} KB)
                </P>
              </>
            ) : (
              <>
                <P type='2' variant='neutral' withMargin={false}>
                  Arrastrá el archivo aquí o hacé clic para seleccionar
                </P>
                <P type='3' variant='neutral' withMargin={false}>
                  Formato: CSV (máx. {MAX_FILE_SIZE_MB}MB)
                </P>
              </>
            )}
          </Card>
        </Card>

        {error && (
          <Card margin={{ bottom: '3' }} spacing="3" variant="danger" modifier="weak">
            {error}
          </Card>
        )}

        {success && (
          <Card margin={{ bottom: '3' }} spacing="3" variant="success" modifier="weak">
            {success}
          </Card>
        )}
      </Card>
    </Modal>
  );
};

export default CasfogUploadModal;
